import { useEffect, useState } from 'react';
import { todayIn } from '@njpos/core';
import { api, loadSession, sizeLabel, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';

const LABEL: Record<string, string> = { ubereats: 'Uber Eats', doordash: 'DoorDash' };
const HIDDEN: Record<string, string> = {
  below_cost: 'Delivery price is under legal cost', not_listed: 'Unticked “Sell on delivery apps”', no_price: 'No price', inactive: 'Inactive',
};
const STATUS: Record<string, string> = { accepted: 'good', completed: 'good', rejected: 'bad', cancelled: 'warn' };

export default function Delivery() {
  const s = loadSession()!;
  const isOwner = s.user.role === 'owner';
  const channels = useLoad(() => api<any[]>('/api/delivery/channels'), []);
  const menu = useLoad(() => api<any>('/api/delivery/menu'), []);
  const orders = useLoad(() => api<any[]>('/api/delivery/orders'), []);
  const summary = useLoad(() => api<any>('/api/delivery/summary'), []);
  const [err, setErr] = useState<unknown>(null);
  const [msg, setMsg] = useState('');
  const [modal, setModal] = useState<null | 'order' | 'payout'>(null);
  const reloadAll = () => { channels.reload(); menu.reload(); orders.reload(); summary.reload(); };

  async function download(platform: string) {
    setErr(null);
    try {
      const res = await api<Response>(`/api/delivery/export/${platform}`, { raw: true });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? `${platform}-menu.csv`;
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e); }
  }

  async function sync(platform: string) {
    setErr(null); setMsg('');
    try {
      const r = await api<any>(`/api/delivery/sync/${platform}`, { method: 'POST' });
      setMsg(r.ok ? `Sent ${r.items} items to ${LABEL[platform]}.` : `${LABEL[platform]} refused the update: ${r.error}`);
      channels.reload();
    } catch (e) { setErr(e); }
  }

  return (
    <div className="stack">
      <div className="page-head"><div className="grow"><h1>Delivery apps</h1>
        <p>Sell on Uber Eats and DoorDash from the same items, stock and prices as the register. Each item has a delivery price; NJ’s below-cost rule applies to it too.</p></div>
        <button onClick={() => setModal('order')}>Enter an order</button>
        {isOwner && <button onClick={() => setModal('payout')}>Record a payout</button>}
      </div>
      <ErrorNote error={err} />
      {msg && <div className="notice good">{msg}</div>}

      {summary.data && (
        <div className="row wrap">
          {summary.data.byPlatform.map((p: any) => (
            <div key={p.platform} className="panel" style={{ padding: 16, minWidth: 200 }}>
              <div className="faint">{LABEL[p.platform]} · last 30 days</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{usd(p.net_sales_cents)}</div>
              <div className="faint">{p.orders} orders · {usd(p.net_sales_cents - p.cogs_cents)} gross margin</div>
            </div>
          ))}
          <div className="panel" style={{ padding: 16, minWidth: 200 }}>
            <div className="faint">Apps still owe you</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{usd(summary.data.owedByAppsCents)}</div>
            <div className="faint">Clears when you record their payout</div>
          </div>
        </div>
      )}

      <div className="row wrap" style={{ alignItems: 'stretch' }}>
        {channels.data?.map((c) => <Channel key={c.platform} c={c} canEdit={isOwner} onDownload={() => download(c.platform)} onSync={() => sync(c.platform)} onSaved={reloadAll} />)}
      </div>

      {isOwner && <PricingRules onSaved={() => menu.reload()} />}

      {menu.data && (
        <div className="panel"><div className="panel-head"><h2>What the apps will show</h2></div><div className="panel-body stack-s">
          <p className="muted">{menu.data.items.length} items listed ({menu.data.items.filter((i: any) => !i.inStock).length} showing sold out). {menu.data.hidden.length} kept off the apps.</p>
          {menu.data.hidden.filter((h: any) => h.reason === 'below_cost').length > 0 && (
            <div className="table-wrap"><table className="grid">
              <thead><tr><th>Kept off: delivery price under legal cost</th><th className="n">Delivery price</th><th className="n">Legal cost</th></tr></thead>
              <tbody>{menu.data.hidden.filter((h: any) => h.reason === 'below_cost').map((h: any) => (
                <tr key={h.itemId}><td>{h.title}</td><td className="n">{usd(h.priceCents)}</td><td className="n">{usd(h.legalCostCents)}</td></tr>
              ))}</tbody>
            </table></div>
          )}
          {menu.data.hidden.length > 0 && <p className="faint">Other items kept off: {Object.entries(menu.data.hidden.reduce((m: any, h: any) => ({ ...m, [h.reason]: (m[h.reason] ?? 0) + 1 }), {}))
            .filter(([r]) => r !== 'below_cost').map(([r, n]) => `${HIDDEN[r] ?? r} (${n})`).join(', ') || 'none'}.</p>}
        </div></div>
      )}

      <div className="page-head" style={{ marginBottom: 0 }}><div className="grow"><h2>Orders</h2><p>Each order becomes a sale at the delivery price and takes the bottles out of stock. A cancelled order puts them back.</p></div></div>
      <div className="panel">
        {orders.data?.length === 0 ? <div className="empty">No delivery orders yet.</div> : (
          <div className="table-wrap"><table className="grid">
            <thead><tr><th>When</th><th>App</th><th>Order</th><th>Customer</th><th>Status</th><th className="n">Bottles</th><th className="n">Total</th><th /></tr></thead>
            <tbody>{orders.data?.map((o) => (
              <tr key={o.id}>
                <td className="nowrap">{new Date(o.placed_at).toLocaleString()}</td><td>{LABEL[o.platform]}</td>
                <td className="nowrap">{o.display_id ?? o.external_order_id}</td><td>{o.customer_name ?? ''}</td>
                <td><span className={`chip ${STATUS[o.status] ?? ''}`}>{o.status}</span>{o.reject_reason && <div className="faint">{o.reject_reason}</div>}</td>
                <td className="n">{o.units || ''}</td><td className="n">{o.total_cents != null ? usd(o.total_cents) : ''}</td>
                <td>{o.status === 'accepted' && <button onClick={async () => { try { await api(`/api/delivery/orders/${o.id}/cancel`, { method: 'POST' }); reloadAll(); } catch (e) { setErr(e); } }}>Cancel</button>}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {modal === 'order' && <EnterOrder onClose={() => setModal(null)} onDone={() => { setModal(null); reloadAll(); }} />}
      {modal === 'payout' && <RecordPayout owed={summary.data?.owedByAppsCents ?? 0} onClose={() => setModal(null)} onDone={() => { setModal(null); reloadAll(); }} />}
    </div>
  );
}

function Channel({ c, canEdit, onDownload, onSync, onSaved }: { c: any; canEdit: boolean; onDownload: () => void; onSync: () => void; onSaved: () => void }) {
  const [ext, setExt] = useState(c.externalStoreId ?? '');
  const [err, setErr] = useState<unknown>(null);
  async function save(patch: any) {
    setErr(null);
    try { await api(`/api/delivery/channels/${c.platform}`, { method: 'PUT', body: { externalStoreId: ext || null, ...patch } }); onSaved(); } catch (e) { setErr(e); }
  }
  return (
    <div className="panel" style={{ flex: '1 1 320px' }}><div className="panel-head"><h2>{c.label}</h2>
      <span className={`chip ${c.enabled ? 'good' : 'warn'}`}>{c.enabled ? 'On' : 'Off'}</span></div>
      <div className="panel-body stack-s">
        <ErrorNote error={err} />
        {c.apiConnected
          ? <p className="muted">Connected. Prices and stock are sent every 15 minutes, and orders come in by themselves.</p>
          : <p className="muted">Automatic sync starts once {c.label} approves PourPoint.AI as a partner. Until then, download the menu spreadsheet for {c.label}’s team and type orders in with “Enter an order”.</p>}
        <label>{c.label} store id<input value={ext} disabled={!canEdit} onChange={(e) => setExt(e.target.value)} placeholder="From your merchant portal" /></label>
        <div className="row wrap">
          <button onClick={onDownload}>Download menu spreadsheet</button>
          {canEdit && <button onClick={() => save({ enabled: !c.enabled })}>{c.enabled ? 'Turn off' : 'Turn on'}</button>}
          {canEdit && ext !== (c.externalStoreId ?? '') && <button onClick={() => save({})}>Save store id</button>}
          {c.apiConnected && c.enabled && <button className="primary" onClick={onSync}>Send prices and stock now</button>}
        </div>
        {c.lastSyncAt && <p className={c.lastSyncStatus === 'error' ? 'notice bad' : 'faint'}>Last sent {new Date(c.lastSyncAt).toLocaleString()}: {c.lastSyncMessage}</p>}
      </div>
    </div>
  );
}

function PricingRules({ onSaved }: { onSaved: () => void }) {
  const s = loadSession()!;
  const [d, setD] = useState<{ markupPct: string; minStock: string } | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [ok, setOk] = useState(false);
  useEffect(() => {
    api<any>('/api/me').then((me) => {
      const st = me.stores.find((x: any) => x.id === s.storeId);
      setD({ markupPct: String(st?.settings?.delivery?.markupPct ?? 0), minStock: String(st?.settings?.delivery?.minStock ?? 0) });
    }).catch(setErr);
  }, [s.storeId]);
  if (!d) return null;
  return (
    <div className="panel"><div className="panel-head"><h2>Delivery pricing</h2></div><div className="panel-body stack-s">
      <ErrorNote error={err} />
      <p className="muted">The apps keep a commission on each order, so many stores charge a little more on delivery. Items without their own delivery price use the shelf price plus this markup, rounded up to .99.</p>
      <div className="field-row">
        <label>Delivery markup (%)<input inputMode="decimal" value={d.markupPct} onChange={(e) => { setOk(false); setD({ ...d, markupPct: e.target.value }); }} /></label>
        <label>Bottles to keep back for walk-ins<input inputMode="numeric" value={d.minStock} onChange={(e) => { setOk(false); setD({ ...d, minStock: e.target.value.replace(/\D/g, '') }); }} /></label>
      </div>
      <div className="row"><button className="primary" onClick={async () => {
        setErr(null);
        try { await api(`/api/stores/${s.storeId}`, { method: 'PATCH', body: { delivery: { markupPct: Number(d.markupPct) || 0, minStock: Number(d.minStock) || 0 } } }); setOk(true); onSaved(); } catch (e) { setErr(e); }
      }}>Save</button>{ok && <span className="faint">Saved.</span>}</div>
    </div></div>
  );
}

function EnterOrder({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [platform, setPlatform] = useState('ubereats');
  const [orderNo, setOrderNo] = useState('');
  const [customer, setCustomer] = useState('');
  const [q, setQ] = useState('');
  const [lines, setLines] = useState<{ item: any; qty: number }[]>([]);
  const [err, setErr] = useState<unknown>(null);
  const found = useLoad(() => (q.length >= 2 ? api<any[]>(`/api/items?limit=8&q=${encodeURIComponent(q)}`) : Promise.resolve([])), [q]);
  const add = (item: any) => { setLines((ls) => ls.some((l) => l.item.id === item.id) ? ls.map((l) => l.item.id === item.id ? { ...l, qty: l.qty + 1 } : l) : [...ls, { item, qty: 1 }]); setQ(''); };
  async function save() {
    setErr(null);
    try {
      await api('/api/delivery/orders', { body: { platform, externalOrderId: orderNo.trim(), customerName: customer || null, lines: lines.map((l) => ({ itemId: l.item.id, qty: l.qty })) } });
      onDone();
    } catch (e) { setErr(e); }
  }
  return (
    <Modal title="Enter a delivery order" onClose={onClose} wide footer={<button className="primary" disabled={!orderNo.trim() || !lines.length} onClick={save}>Save order</button>}>
      <ErrorNote error={err} />
      <p className="faint">Copy the order from the app’s tablet. It is charged at each item’s delivery price, and the app’s courier checks the customer’s ID at the door.</p>
      <div className="field-row">
        <label>App<select value={platform} onChange={(e) => setPlatform(e.target.value)}><option value="ubereats">Uber Eats</option><option value="doordash">DoorDash</option></select></label>
        <label>Order number<input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} /></label>
        <label>Customer first name<input value={customer} onChange={(e) => setCustomer(e.target.value)} /></label>
      </div>
      <label>Add an item<input value={q} placeholder="Name or barcode" onChange={(e) => setQ(e.target.value)} /></label>
      {q.length >= 2 && found.data?.map((i) => (
        <div key={i.id} className="row clickable" onClick={() => add(i)}>{i.brand} {i.name} {sizeLabel(i.size_ml)} <span className="faint">on hand {i.on_hand}</span></div>
      ))}
      {lines.length > 0 && <table className="grid"><tbody>{lines.map((l) => (
        <tr key={l.item.id}><td>{l.item.brand} {l.item.name} {sizeLabel(l.item.size_ml)}</td>
          <td className="n"><input style={{ width: 70 }} inputMode="numeric" value={l.qty} onChange={(e) => setLines(lines.map((x) => x.item.id === l.item.id ? { ...x, qty: Math.max(1, Number(e.target.value.replace(/\D/g, '')) || 1) } : x))} /></td>
          <td><button onClick={() => setLines(lines.filter((x) => x.item.id !== l.item.id))}>Remove</button></td></tr>
      ))}</tbody></table>}
    </Modal>
  );
}

function RecordPayout({ owed, onClose, onDone }: { owed: number; onClose: () => void; onDone: () => void }) {
  const [platform, setPlatform] = useState('ubereats');
  const [deposit, setDeposit] = useState('');
  const [fees, setFees] = useState('');
  const [paidOn, setPaidOn] = useState(todayIn());
  const [err, setErr] = useState<unknown>(null);
  const c = (v: string) => Math.round((Number(v) || 0) * 100);
  return (
    <Modal title="Record a payout from a delivery app" onClose={onClose} footer={<button className="primary" disabled={!deposit} onClick={async () => {
      setErr(null);
      try { await api('/api/delivery/payouts', { body: { platform, depositCents: c(deposit), feesCents: c(fees), paidOn } }); onDone(); } catch (e) { setErr(e); }
    }}>Record payout</button>}>
      <ErrorNote error={err} />
      <p className="faint">Use the payout statement from the app. The deposit goes to the bank, the commission goes to “Delivery app fees”, and together they clear what the app owed you ({usd(owed)} right now, across both apps).</p>
      <div className="field-row">
        <label>App<select value={platform} onChange={(e) => setPlatform(e.target.value)}><option value="ubereats">Uber Eats</option><option value="doordash">DoorDash</option></select></label>
        <label>Paid on<input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} /></label>
      </div>
      <div className="field-row">
        <label>Deposit to bank ($)<input className="num-input" inputMode="decimal" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></label>
        <label>Commission and fees kept ($)<input className="num-input" inputMode="decimal" value={fees} onChange={(e) => setFees(e.target.value)} /></label>
      </div>
    </Modal>
  );
}
